import { debugLogger } from './debugLogger';

/**
 * Validates project data uploaded via Excel template
 * @param {Array} projectData - Array of data rows from the Data sheet
 * @param {Object} projectDetails - Project info from the Project Info sheet
 * @returns {Object} Validation results with isValid flag, errors array, and warnings array
 */
export const validateProjectData = (projectData, projectDetails) => {
  debugLogger.log('VALIDATION_UPLOAD', 'Starting project data validation', {
    rowCount: projectData?.length,
    hasProjectDetails: !!projectDetails
  });
  
  const errors = [];
  const warnings = [];

  // Validate project details
  if (!projectDetails) {
    errors.push({
      message: 'Project Info sheet is missing or invalid',
    });
    return { isValid: false, errors, warnings };
  }

  // Required fields in project details — Project Title is validated separately
  // via the form field guard; only date fields need to be present here
  const requiredProjectFields = ['Start Date', 'End Date'];
  requiredProjectFields.forEach(field => {
    if (!projectDetails[field] || String(projectDetails[field]).trim() === '') {
      errors.push({
        field: field,
        message: `${field} is required in Project Info`,
      });
    }
  });

  // Validate project data
  if (!projectData || !Array.isArray(projectData) || projectData.length === 0) {
    errors.push({
      message: 'Data sheet is missing or contains no rows',
    });
    return { isValid: false, errors, warnings };
  }

  // Required fields in each data row
  const requiredDataFields = [
    'Entity ID',
    'Entity',
    'Department ID',
    'Department Name',
    'Account ID',
    'Account Name',
  ];

  // Optional/metadata fields that are deliberately excluded from date column detection.
  // These columns contain business metadata (not time periods), so they are ignored when
  // inferring which headers represent date-based value columns in the Data sheet.
  const NON_DATE_FIELDS = ['Parent Account', 'Acct Type', 'Fixed or Variable', 'Driver'];

  // ── Column structure validation ──────────────────────────────────────────────
  // All 10 required non-date columns must be present, and every non-date column
  // must appear before every date column in the header order.
  const ALL_NON_DATE_COLS = [...requiredDataFields, ...NON_DATE_FIELDS];
  const MMM_YYYY_RE = /^[A-Za-z]{3} \d{4}$/; // matches normalised 'MMM yyyy' headers

  if (projectData.length > 0) {
    const headers = Object.keys(projectData[0]);

    // 1. Check all required non-date columns are present
    // Required: the 6 fields that must have data. Optional: the 4 metadata fields.
    const missingRequired = requiredDataFields.filter(col => !headers.includes(col));
    const missingOptional = NON_DATE_FIELDS.filter(col => !headers.includes(col));

    if (missingRequired.length > 0) {
      errors.push({
        type: 'dataset',
        message: `The following required columns are missing from your file: ${missingRequired.join(', ')}. ` +
          `Your file must include all 10 account columns (Entity ID, Entity, Department ID, Department Name, ` +
          `Account ID, Account Name, Parent Account, Acct Type, Fixed or Variable, Driver) followed by your date columns.`,
      });
    }
    if (missingOptional.length > 0) {
      warnings.push({
        type: 'dataset',
        message: `The following optional columns are missing: ${missingOptional.join(', ')}. ` +
          `These columns are recommended (Parent Account, Acct Type, Fixed or Variable, Driver).`,
      });
    }

    // 2. Check that all non-date columns appear before all date columns
    // A column is considered a date column if it matches MMM yyyy (already normalised
    // by the time validation runs) or if it is not in the known non-date set.
    const isDateCol = (h) => MMM_YYYY_RE.test(h) && !ALL_NON_DATE_COLS.includes(h);
    const isNonDateCol = (h) => ALL_NON_DATE_COLS.includes(h);

    let firstDateIdx = headers.findIndex(isDateCol);
    let lastNonDateIdx = -1;
    headers.forEach((h, i) => { if (isNonDateCol(h)) lastNonDateIdx = i; });

    if (firstDateIdx !== -1 && lastNonDateIdx !== -1 && lastNonDateIdx > firstDateIdx) {
      // Find the specific offending columns for a helpful message
      const outOfOrder = headers
        .slice(firstDateIdx)
        .filter(isNonDateCol);
      errors.push({
        type: 'dataset',
        message: `Column order is incorrect. All account columns must appear before date columns. ` +
          `The following account column(s) appear after date columns: ${outOfOrder.join(', ')}. ` +
          `Expected order: account columns first (Entity ID, Entity, …, Driver), then date columns (Jan 2024, Feb 2024, …).`,
      });
    }
  }
  // ─────────────────────────────────────────────────────────────────────────────

  // Validate each data row
  projectData.forEach((row, index) => {
    const rowNumber = index + 2; // +2 because row 1 is headers, Excel is 1-indexed

    // Check required fields
    requiredDataFields.forEach(field => {
      if (!row[field] || String(row[field]).trim() === '') {
        errors.push({
          row: rowNumber,
          field: field,
          message: `${field} is required but missing or empty`,
        });
      }
    });

    // Validate numeric fields
    const numericFields = ['Entity ID', 'Department ID', 'Account ID'];
    numericFields.forEach(field => {
      if (row[field] !== undefined && row[field] !== null && row[field] !== '') {
        const value = String(row[field]).trim();
        if (isNaN(value)) {
          errors.push({
            row: rowNumber,
            field: field,
            message: `${field} must be a number, got "${value}"`,
          });
        }
      }
    });

    // Validate Account Type field
    if (row['Acct Type']) {
      const validTypes = ['Revenue', 'Expense', 'Deduction', 'Other Revenue', 'Asset', 'Liability', 'Equity', 'Stat', 'P&L'];
      if (!validTypes.includes(row['Acct Type'])) {
        warnings.push({
          row: rowNumber,
          field: 'Acct Type',
          message: `Account Type "${row['Acct Type']}" is not standard. Expected one of: ${validTypes.join(', ')}`,
        });
      }
    }

    // Validate Fixed or Variable field
    if (row['Fixed or Variable']) {
      const validValues = ['Fixed', 'Variable', 'Stat', ''];
      if (!validValues.includes(row['Fixed or Variable'])) {
        warnings.push({
          row: rowNumber,
          field: 'Fixed or Variable',
          message: `Value "${row['Fixed or Variable']}" should be "Fixed", "Variable", "Stat", or empty`,
        });
      }
    }

    // Check for empty Driver field (context-aware warning)
    const fixedOrVariable = row['Fixed or Variable'];
    const acctType = row['Acct Type'];
    
    if (!row['Driver'] || String(row['Driver']).trim() === '') {
      // Only warn for Variable accounts that typically need drivers
      if (fixedOrVariable === 'Variable' && acctType !== 'Stat') {
        warnings.push({
          row: rowNumber,
          field: 'Driver',
          message: `Variable ${acctType} account has no driver. Consider adding a stat driver for forecasting.`,
        });
      }
    }

    // Validate that at least one date column has data
    const dateColumns = Object.keys(row).filter(key => 
      !requiredDataFields.includes(key) && 
      !NON_DATE_FIELDS.includes(key)
    );
    
    const hasDateData = dateColumns.some(col => {
      const value = row[col];
      return value !== undefined && value !== null && value !== '';
    });

    if (dateColumns.length > 0 && !hasDateData) {
      warnings.push({
        row: rowNumber,
        message: 'No data found in any date columns for this row',
      });
    }

    // Validate numeric values in date columns
    dateColumns.forEach(col => {
      if (row[col] !== undefined && row[col] !== null && row[col] !== '') {
        const value = String(row[col]).trim();
        if (isNaN(value)) {
          errors.push({
            row: rowNumber,
            field: col,
            message: `Date column "${col}" contains non-numeric value "${value}"`,
          });
        }
      }
    });
  });

  // ========== CROSS-ROW CONSISTENCY VALIDATIONS ==========
  
  // Build maps of ID -> { name, firstRow } for consistency checking
  const entityMappings = {}; // entityId -> { name, firstRow }
  const deptMappings = {}; // "entityId-deptId" -> { name, firstRow, entityName }
  const accountMappings = {}; // "entityId-deptId-acctId" -> { name, firstRow, deptName }

  projectData.forEach((row, index) => {
    const rowNum = index + 2;
    const entityId = String(row['Entity ID'] || '').trim();
    const entityName = String(row['Entity'] || '').trim();
    const deptId = String(row['Department ID'] || '').trim();
    const deptName = String(row['Department Name'] || '').trim();
    const acctId = String(row['Account ID'] || '').trim();
    const acctName = String(row['Account Name'] || '').trim();
    
    // Skip if entity ID is empty (will be caught by required field validation)
    if (!entityId) return;
    
    // Check Entity ID consistency
    if (entityMappings[entityId]) {
      // We've seen this Entity ID before - names must match exactly
      if (entityMappings[entityId].name !== entityName) {
        errors.push({
          row: rowNum,
          field: 'Entity',
          message: `Inconsistent Entity name for Entity ID ${entityId}. Row ${entityMappings[entityId].firstRow} has "${entityMappings[entityId].name}", but row ${rowNum} has "${entityName}". The same Entity ID must have the same name throughout the file.`
        });
      }
    } else {
      // First occurrence - store it as the "truth"
      entityMappings[entityId] = {
        name: entityName,
        firstRow: rowNum
      };
    }
    
    // Skip if department ID is empty (will be caught by required field validation)
    if (!deptId) return;
    
    // Check Department ID consistency (scoped by entity)
    const deptKey = `${entityId}-${deptId}`;
    if (deptMappings[deptKey]) {
      if (deptMappings[deptKey].name !== deptName) {
        errors.push({
          row: rowNum,
          field: 'Department Name',
          message: `Inconsistent Department name for Department ID ${deptId} in Entity "${entityName}". Row ${deptMappings[deptKey].firstRow} has "${deptMappings[deptKey].name}", but row ${rowNum} has "${deptName}". The same Department ID must have the same name within an entity.`
        });
      }
    } else {
      deptMappings[deptKey] = {
        name: deptName,
        firstRow: rowNum,
        entityName: entityName
      };
    }
    
    // Skip if account ID is empty (will be caught by required field validation)
    if (!acctId) return;
    
    // Check Account ID consistency (scoped by entity + department / GL Key)
    const glKey = `${entityId}-${deptId}-${acctId}`;
    if (accountMappings[glKey]) {
      if (accountMappings[glKey].name !== acctName) {
        errors.push({
          row: rowNum,
          field: 'Account Name',
          message: `Inconsistent Account name for Account ID ${acctId} in Department "${deptName}". Row ${accountMappings[glKey].firstRow} has "${accountMappings[glKey].name}", but row ${rowNum} has "${acctName}". The same Account ID must have the same name within a department.`
        });
      }
    } else {
      accountMappings[glKey] = {
        name: acctName,
        firstRow: rowNum,
        deptName: deptName
      };
    }
  });

  const result = {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
  
  debugLogger.log('VALIDATION_UPLOAD', 'Validation complete', {
    isValid: result.isValid,
    errorCount: errors.length,
    warningCount: warnings.length,
    errors: errors.slice(0, 5), // Log first 5 errors
    warnings: warnings.slice(0, 5) // Log first 5 warnings
  });
  
  if (!result.isValid) {
    debugLogger.warn('VALIDATION_UPLOAD', 'Validation failed', {
      errorCount: errors.length,
      firstError: errors[0]
    });
  }
  
  return result;
};
